// Route views for the portal React app

import React, { useState, useEffect, useRef } from 'react';
import { AppState, badgeForMaint, uniq, fmtDate, matchesGlobal, BadgeForStatus, severityClass, todayStr, nowStr } from './state';
import { MY_PRODUCTS_QUERY } from '../graphql/myProducts';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client/react';
import { GENERATE_ISO_DOWNLOAD_LINK } from "../graphql/generateIsoDownloadLink";
import { MY_DOWNLOADS_QUERY } from '../graphql/myDownloads';
import { MY_REPOS_QUERY, REPO_CONTENTS_QUERY } from '../graphql/myRepos';
import { REALITY_SEARCH_QUERY } from '../graphql/realitySearch';
import { REALITY_DOCUMENTS_QUERY } from '../graphql/realityDocuments';
import {MY_ORGANIZATION_QUERY, ORGANIZATION_MEMBERS_QUERY, ORGANIZATION_INVITATIONS_QUERY, INVITE_ORGANIZATION_USER, CANCEL_ORGANIZATION_INVITATION, RESEND_ORGANIZATION_INVITATION, DISABLE_ORGANIZATION_USER, ENABLE_ORGANIZATION_USER, CHANGE_ORGANIZATION_USER_ROLE, } from "../graphql/organization";


interface DashboardViewProps {
  state: AppState;
  setRoute: (route: string) => void;
}

export function DashboardView({ state, setRoute }: DashboardViewProps): React.ReactElement {
  const maintRed = badgeForMaint("RedHawk Linux", state.org);
  const maintSim = badgeForMaint("SIMulation Workbench", state.org);

  const expiringCount = Object.values(state.org.maintenance)
    .filter(m => m.status === "ExpiringSoon").length;
  const openTickets = state.tickets.filter(t => t.status !== "Closed").length;
  const latestReleases = [...state.releases]
    .sort((a,b) => (a.date < b.date ? 1 : -1))
    .slice(0, 3);
  const latestDocs = state.docs.slice(0, 3);

  return (
    <div className="grid">
      <div className="card" style={{ gridColumn: "span 8" }}>
        <div className="cardHeader">
          <div>
            <h3>At a glance</h3>
            <p>Org status, coverage, and activity.</p>
          </div>
          <span className="badge">{state.org.name}</span>
        </div>
        <div className="cardBody">
          <div className="statRow">
            <div className="stat">
              <div className="label">Open tickets</div>
              <div className="value">{openTickets}</div>
              <div className="sub">Includes "Waiting on Customer".</div>
            </div>
            <div className="stat">
              <div className="label">Coverage expiring soon</div>
              <div className="value">{expiringCount}</div>
              <div className="sub">Review renewals to avoid support interruptions.</div>
            </div>
            <div className="stat">
              <div className="label">Last download activity</div>
              <div className="value">2 days ago</div>
              <div className="sub">Based on download history (mock).</div>
            </div>
          </div>
          <div style={{ height: 14 }} />
          <div className="filters">
            <span className="badge ok">● RedHawk Linux</span>
            {maintRed}
            <span className="badge ok">● SIMulation Workbench</span>
            {maintSim}
          </div>
        </div>
      </div>

      <div className="card" style={{ gridColumn: "span 4" }}>
        <div className="cardHeader">
          <div>
            <h3>Quick actions</h3>
            <p>Common tasks for engineers.</p>
          </div>
        </div>
        <div className="cardBody" style={{ display:"flex", flexDirection:"column", gap:10 }}>
          <button className="btn primary" onClick={() => window.dispatchEvent(new CustomEvent("open-ticket-modal"))}>
            Submit a support ticket
          </button>
          <button className="btn" onClick={() => setRoute("downloads")}>Find downloads</button>
          <button className="btn" onClick={() => setRoute("docs")}>Search documentation</button>
          <button className="btn" onClick={() => {window.location.href = "/knowledge-base/"; }}> Document Library Site</button>
          <button className="btn" onClick={() => setRoute("account")}>Manage account</button>

          <div className="empty">
            Tip: Use the global search to find docs, downloads, and tickets with one query.
          </div>
        </div>
      </div>

      <div className="card" style={{ gridColumn: "span 7" }}>
        <div className="cardHeader">
          <div>
            <h3>Latest releases</h3>
            <p>Recent production releases (mock).</p>
          </div>
          <button className="btn" onClick={() => setRoute("downloads")}>View all</button>
        </div>
        <div className="cardBody">
          <table className="table">
            <thead>
              <tr><th>Product</th><th>Version</th><th>Release date</th><th>Notes</th></tr>
            </thead>
            <tbody>
              {latestReleases.map(r => (
                <tr key={r.id}>
                  <td>{r.product}</td>
                  <td><span className="badge">{r.version}</span></td>
                  <td>{fmtDate(r.date)}</td>
                  <td style={{ color:"rgba(255,255,255,.75)" }}>{r.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

       <div className="card" style={{ gridColumn: "span 5" }}>
        <div className="cardHeader">
          <div>
            <h3>Featured documentation</h3>
            <p>Helpful articles based on common workflows.</p>
          </div>
          <button className="btn" onClick={() => setRoute("docs")}>Browse</button>
        </div>
        <div className="cardBody" style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {latestDocs.map(d => (
            <div key={d.id} className="stat" style={{ minWidth:"auto" }}>
              <div className="label">{d.product} · {d.category}</div>
              <div className="value" style={{ fontSize:14 }}>{d.title}</div>
              <div className="sub">
                {d.version} · <span style={{ fontFamily:"var(--mono)" }}>{d.id}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


// Products Section 


interface ProductsViewProps {
  state: AppState;
  setRoute: (route: string) => void;
}

interface ProductItem {
  id: number;
  customer_id: number | null;
  product_id: number | null;
  selected_version: string | null;
  sold_on: string | null;
  maint_through: string | null;
  name: string | null;
  description: string | null;
  model_num: string | null;
  arch: string | null;
  flags: number | null;
}

interface MyProductsData {
  myProducts: ProductItem[];
}

interface MyProductsVars {
  portalUserId: number;
}

export function ProductsView({
  state,
  setRoute,
}: ProductsViewProps): React.ReactElement {
  const storedUser = localStorage.getItem("user");
  const portalUser = storedUser ? JSON.parse(storedUser) : null;

  const { data, loading, error } = useQuery<MyProductsData, MyProductsVars>(
    MY_PRODUCTS_QUERY,
    {
      variables: {
        portalUserId: portalUser?.id,
      },
      skip: !portalUser?.id,
    }
  );

  const products = data?.myProducts ?? [];

  return (
    <div className="grid">
      <div className="card" style={{ gridColumn: "span 12" }}>
        <div className="cardHeader">
          <div>
            <h3>Your products</h3>
            <p>Entitlements and maintenance/support status.</p>
          </div>
          <span className="badge">{products.length} products</span>
        </div>

        <div className="cardBody">
          {loading && <div className="empty">Loading products...</div>}

          {error && (
            <div className="empty">
              Error loading products. Check your GraphQL query or backend resolver.
            </div>
          )}

          {!loading && !error && products.length === 0 && (
            <div className="empty">
              No products found for this portal user.
            </div>
          )}

        
          <div className="productsScroll">
          <div className="grid" style={{ gap: 14 }}>
            {products.map((p) => {
              
              return (
                <div
                  key={p.id}
                  className="card"
                  style={{
                    gridColumn: "span 6",
                    boxShadow: "none",
                    background: "rgba(0,0,0,.10)",
                  }}
                >
                  <div className="cardHeader">
                    <div>
                      <h3>{p.name || "Unknown Product"}</h3>
                      <p>
                        Model:{" "}
                        <span style={{ fontFamily: "var(--mono)" }}>
                          {p.model_num || "—"}
                        </span>
                        {" · "}
                        Arch: {p.arch || "—"}
                      </p>
                    </div>

                    <span className="badge">
                      Version: {p.selected_version || "—"}
                    </span>
                  </div>

                  <div className="cardBody">
                    <div className="statRow">
                      <div className="stat">
                        <div className="label">Customer ID</div>
                        <div className="value">{p.customer_id || "—"}</div>
                      </div>

                      <div className="stat">
                        <div className="label">Sold On</div>
                        <div className="value"> <div className="value">
  {p.sold_on
    ? new Date(p.sold_on).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "—"}
</div></div>
                      </div>

                      <div className="stat">
                        <div className="label">Maintenance Through</div>
                        <div className="value">{p.maint_through
    ? new Date(p.maint_through).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "—"}</div>
                      </div>
                    </div>

                    <div style={{ height: 12 }} />

                    <div className="empty">
                      <strong>Description</strong>
                      <br />
                      {p.description || "No description available."}
                    </div>

                    <div style={{ height: 12 }} />

                    <div className="filters">
                      <button
                        className="btn primary"
                        onClick={() => setRoute("downloads")}
                      >
                        Downloads
                      </button>

                      <button
                        className="btn"
                        onClick={() => setRoute("docs")}
                      >
                        Documentation
                      </button>

                      <button
                        className="btn"
                        onClick={() => setRoute("support")}
                      >
                        Support
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
    </div>
  );
}

//Donwloads Tab

interface DownloadsViewProps {
  state: AppState;
  setState: React.Dispatch<React.SetStateAction<AppState>>;
}

interface IsoFile {
  id: number;
  product_id: number | null;
  product_name: string | null;
  model_num: string | null;
  path: string | null;
  arch: string | null;
  label: string | null;
  version: string | null;
  size: string | null;
}

interface RepoFile {
  id: number;
  product_id: number | null;
  product_name: string | null;
  model_num: string | null;
  path: string | null;
  arch: string | null;
  release_id: number | null;
}

interface RepoItem {
  name: string;
  type: string;
  size: string | null;
  modifiedAt: string | null;
}

interface RepoContentsData {
  repoContents: RepoItem[];
}

interface RepoContentsVars {
  portalUserId: number;
  productId: number;
  subPath?: string | null;
}

interface MyDownloadsData {
  myIsos: IsoFile[];
  // myRepos: RepoFile[];
}

interface MyDownloadsVars {
  portalUserId: number;
}

interface GenerateIsoDownloadLinkData {
  generateIsoDownloadLink: {
    url: string;
    filename: string;
  };
}

interface GenerateIsoDownloadLinkVars {
  portalUserId: number;
  productId: number;
}


export function DownloadsView({
  state,
  setState,
}: DownloadsViewProps): React.ReactElement {
  const storedUser = localStorage.getItem("user");
  const portalUser = storedUser ? JSON.parse(storedUser) : null;

  const { data, loading, error } = useQuery<
    MyDownloadsData,
    MyDownloadsVars
  >(MY_DOWNLOADS_QUERY, {
    variables: {
      portalUserId: portalUser?.id ?? 0,
    },
    skip: !portalUser?.id,
  });

// const [generateIsoDownloadLink, { loading: generatingLink }] = useMutation<
//   GenerateIsoDownloadLinkData,
//   GenerateIsoDownloadLinkVars
// >(GENERATE_ISO_DOWNLOAD_LINK);

const handleIsoDownload = (
  isoId: string | number,
  portalUserId: string | number
): void => {
  const url =
    `/api/downloads/iso/${isoId}` +
    `?portalUserId=${portalUserId}`;

  window.location.href = url;
};
const downloads = [
  ...(data?.myIsos ?? []).map((iso) => ({
    id: `${iso.id}`,
    productId: iso.product_id,
    productName: iso.product_name || "Unknown Product",
    version: iso.version || "—",
    platform: iso.arch || "—",
    type: "ISO",
    size: iso.size || "—",
    file: iso.path || "",
    access: "Entitled",
  })),

];

const productOptions = [
  "All",
  ...uniq(downloads.map((d) => d.productName)),
];
  const versions = ["All", ...uniq(downloads.map((d) => d.version))];
  const platforms = ["All", ...uniq(downloads.map((d) => d.platform))];
  const types = ["All", ...uniq(downloads.map((d) => d.type))];

const filtered = downloads.filter((d) => {
  const f = state.downloadFilters;

  return (
    (f.product === "All" || d.productName === f.product) &&
    (f.version === "All" || d.version === f.version) &&
    (f.platform === "All" || d.platform === f.platform) &&
    (f.type === "All" || d.type === f.type) &&
    matchesGlobal(d, state.globalQuery)
  );
});

  const updateFilters = (
    patch: Partial<typeof state.downloadFilters>
  ): void => {
    setState((prev) => ({
      ...prev,
      downloadFilters: { ...prev.downloadFilters, ...patch },
    }));
  };

  return (
    <div className="grid">
      <div className="card" style={{ gridColumn: "span 12" }}>
        <div className="cardHeader">
          <div>
            <h3>Downloads</h3>
            <p>Downloads available based on customer entitlement.</p>
          </div>
          <span className="badge">Showing {filtered.length} items</span>
        </div>

        <div className="cardBody">
          {loading && <div className="empty">Loading downloads...</div>}

          {error && (
            <div className="empty">
              Error loading downloads. Check your GraphQL schema/resolver.
            </div>
          )}

          {!loading && !error && (
            <>
              <div className="filters">
                <div className="field">
                <label>Product</label>
                          <select
                          style={{ backgroundColor: "white" }}
            value={state.downloadFilters.product}
            onChange={(e) =>
              updateFilters({
                product: e.target.value,
                version: "All",
              })
            }
          >
            {productOptions.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
                </div>

                <div className="field">
                  <label>Version</label>
                  <select
                  style={{ backgroundColor: "white" }}
                    value={state.downloadFilters.version}
                    onChange={(e) =>
                      updateFilters({ version: e.target.value })
                    }
                  >
                    {versions.map((v) => (
                      <option key={v} value={v}>
                        {v}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="field">
                  <label>Platform</label>
                  <select
                  style={{ backgroundColor: "white" }}
                    value={state.downloadFilters.platform}
                    onChange={(e) =>
                      updateFilters({ platform: e.target.value })
                    }
                  >
                    {platforms.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="field">
                  <label>Type</label>
                  <select
                  style={{ backgroundColor: "white" }}
                    value={state.downloadFilters.type}
                    onChange={(e) => updateFilters({ type: e.target.value })}
                  >
                    {types.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ height: 14 }} />

              <table className="table">
                <thead>
                  <tr>
                    <th>Product/File</th>
                    <th>Version</th>
                    <th>Platform</th>
                    <th>Type</th>
                    <th>Size</th>
                    <th>Access</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {filtered.map((d) => (
                    <tr key={d.id}>
                     <td>{d.productName}</td>
                      <td>
                        <span className="badge">{d.version}</span>
                      </td>
                      <td>{d.platform}</td>
                      <td>{d.type}</td>
                      <td>{d.size}</td>
                      <td>
                        <span className="badge ok">● {d.access}</span>
                      </td>
                      <td>
              <button
  className="btn"
  disabled={!d.id}
  onClick={() => {
    if (!d.id) {
      alert("No ISO is associated with this download.");
      return;
    }

    console.log("Download object", d);
        console.log("Download object", d.id);

            console.log("Download object", portalUser.id);


    handleIsoDownload(
      d.id,
      portalUser.id
    );
  }}
>
  Download
</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filtered.length === 0 && (
                <>
                  <div style={{ height: 12 }} />
                  <div className="empty">
                    No downloads found for this portal user.
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}



// Repos Tab

interface RepoAccess {
  id: number;
  product_id: number;
  product_name: string;
  model_num: string | null;
  version: string | null;
  arch: string | null;
  repo_path: string;
  repo_url: string;
}

interface MyReposData {
  myRepos: RepoAccess[];
}

interface MyReposVars {
  portalUserId: number;
}

interface ReposViewProps {
  state: AppState;
  setState: React.Dispatch<React.SetStateAction<AppState>>;
}

export function ReposView({
  state,
  setState,
}: ReposViewProps): React.ReactElement {
  const storedUser = localStorage.getItem("user");
  const portalUser = storedUser ? JSON.parse(storedUser) : null;

  const { data, loading, error } = useQuery<MyReposData, MyReposVars>(
    MY_REPOS_QUERY,
    {
      variables: {
        portalUserId: portalUser?.id ?? 0,
      },
      skip: !portalUser?.id,
    }
  );

  const repos = data?.myRepos ?? [];

  const [selectedRepo, setSelectedRepo] =
  React.useState<RepoAccess | null>(null);

const [repoSubPath, setRepoSubPath] =
  React.useState("");

const [
  loadRepoContents,
  {
    data: repoContentsData,
    loading: repoContentsLoading,
    error: repoContentsError,
  },
] = useLazyQuery<
  RepoContentsData,
  RepoContentsVars
>(REPO_CONTENTS_QUERY);

  const productOptions = [
    "All",
    ...uniq(repos.map((r) => r.product_name)),
  ];

  const versions = [
    "All",
    ...uniq(repos.map((r) => r.version || "—")),
  ];

  const platforms = [
    "All",
    ...uniq(repos.map((r) => r.arch || "—")),
  ];

  const filtered = repos.filter((r) => {
    const f = state.downloadFilters;

    return (
      (f.product === "All" || r.product_name === f.product) &&
      (f.version === "All" || (r.version || "—") === f.version) &&
      (f.platform === "All" || (r.arch || "—") === f.platform) &&
      matchesGlobal(r, state.globalQuery)
    );
  });

  const updateFilters = (
    patch: Partial<typeof state.downloadFilters>
  ): void => {
    setState((prev) => ({
      ...prev,
      downloadFilters: {
        ...prev.downloadFilters,
        ...patch,
      },
    }));
  };

  const browseRepo = (
  repo: RepoAccess
) => {
  if (!portalUser?.id) return;

  setSelectedRepo(repo);
  setRepoSubPath("");

  loadRepoContents({
    variables: {
      portalUserId: portalUser.id,
      productId: repo.product_id,
      subPath: "",
    },
  });
};

const openRepoFolder = (
  folderName: string
) => {
  if (
    !portalUser?.id ||
    !selectedRepo
  ) {
    return;
  }

  const nextPath = [
    repoSubPath,
    folderName,
  ]
    .filter(Boolean)
    .join("/");

  setRepoSubPath(nextPath);

  loadRepoContents({
    variables: {
      portalUserId: portalUser.id,
      productId:
        selectedRepo.product_id,
      subPath: nextPath,
    },
  });
};

const goUpRepoFolder = () => {
  if (
    !portalUser?.id ||
    !selectedRepo
  ) {
    return;
  }

  const parts =
    repoSubPath
      .split("/")
      .filter(Boolean);

  parts.pop();

  const nextPath =
    parts.join("/");

  setRepoSubPath(nextPath);

  loadRepoContents({
    variables: {
      portalUserId: portalUser.id,
      productId:
        selectedRepo.product_id,
      subPath: nextPath,
    },
  });
};

  return (
    <div className="grid">
      <div className="card" style={{ gridColumn: "span 12" }}>
        <div className="cardHeader">
          <div>
            <h3>Repositories</h3>
            <p>Repository access based on active maintenance.</p>
          </div>

          <span className="badge">
            Showing {filtered.length} repos
          </span>
        </div>

        <div className="cardBody">
          {loading && (
            <div className="empty">
              Loading repositories...
            </div>
          )}

          {error && (
            <div className="empty">
              Error loading repositories. Check your GraphQL schema/resolver.
            </div>
          )}

          {!loading && !error && (
            <>
              <div className="filters">
                <div className="field">
                  <label>Product</label>
                  <select
                  style={{ backgroundColor: "white" }}
                    value={state.downloadFilters.product}
                    onChange={(e) =>
                      updateFilters({
                        product: e.target.value,
                        version: "All",
                      })
                    }
                  >
                    {productOptions.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="field">
                  <label>Version</label>
                  <select
                  style={{ backgroundColor: "white" }}
                    value={state.downloadFilters.version}
                    onChange={(e) =>
                      updateFilters({
                        version: e.target.value,
                      })
                    }
                  >
                    {versions.map((v) => (
                      <option key={v} value={v}>
                        {v}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="field">
                  <label>Platform</label>
                  <select
                  style={{ backgroundColor: "white" }}
                    value={state.downloadFilters.platform}
                    onChange={(e) =>
                      updateFilters({
                        platform: e.target.value,
                      })
                    }
                  >
                    {platforms.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ height: 14 }} />

              <table className="table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Version</th>
                    <th>Platform</th>
                    <th>Repository Path</th>
                    <th>Access</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {filtered.map((repo) => (
                    <tr key={repo.id}>
                      <td>{repo.product_name}</td>
                      <td>
                        <span className="badge">
                          {repo.version || "—"}
                        </span>
                      </td>
                      <td>{repo.arch || "—"}</td>
                      <td style={{ fontFamily: "var(--mono)" }}>
                        {repo.repo_path}
                      </td>
                      <td>
                        <span className="badge ok">
                          ● Entitled
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn"
                          onClick={() =>
                            browseRepo(repo)
                          }
                        >
                          Browse Repo
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              

              {selectedRepo && (
  <div
    className="card"
    style={{ marginTop: 24 }}
  >
    <div className="card-header">
      <div>
        <h3>
          {selectedRepo.product_name}
        </h3>

        <div
          style={{
            fontFamily: "var(--mono)",
            fontSize: 13,
          }}
        >
          {selectedRepo.repo_path}
          {repoSubPath
            ? `/${repoSubPath}`
            : ""}
        </div>
      </div>

      <button
        className="btn"
        onClick={() => {
          setSelectedRepo(null);
          setRepoSubPath("");
        }}
      >
        Close
      </button>
    </div>

    {repoSubPath && (
      <div
        style={{
          marginBottom: 12,
        }}
      >
        <button
          className="btn"
          onClick={goUpRepoFolder}
        >
          ← Back
        </button>
      </div>
    )}

    {repoContentsLoading && (
      <div>
        Loading repository...
      </div>
    )}

    {repoContentsError && (
      <div>
        Error loading repository:{" "}
        {repoContentsError.message}
      </div>
    )}

    {!repoContentsLoading &&
      !repoContentsError && (
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Size</th>
              <th>Modified</th>
              <th></th>
            </tr>
          </thead>

          <tbody>
            {(repoContentsData
              ?.repoContents ?? []
            ).map((item) => (
              <tr key={item.name}>
                <td
                  style={{
                    fontFamily:
                      "var(--mono)",
                  }}
                >
                  {item.type === "d"
                    ? "📁 "
                    : "📄 "}
                  {item.name}
                </td>

                <td>
                  {item.type === "d"
                    ? "Directory"
                    : "File"}
                </td>

                <td>
                  {item.size ?? "—"}
                </td>

                <td>
                  {item.modifiedAt
                    ? new Date(
                        item.modifiedAt
                      ).toLocaleString()
                    : "—"}
                </td>

                <td>
                  {item.type === "d" && (
                    <button
                      className="btn"
                      onClick={() =>
                        openRepoFolder(
                          item.name
                        )
                      }
                    >
                      Open
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
  </div>
)}

              {filtered.length === 0 && (
                <>
                  <div style={{ height: 12 }} />
                  <div className="empty">
                    No repositories found for this portal user.
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

//Document Tab

interface RealityHistoryMessage {
  role: "user" | "assistant";
  content: string;
}

interface RealitySource {
  id: string;
  name: string;
  pages: Array<{
    pageIndex: number;
  }>;
}

interface RealitySearchData {
  realitySearch: {
    answer: string;
    sources: RealitySource[];
    pagesViewedCount: number;
    durationMs: number | null;
  };
}

interface RealitySearchVars {
  message: string;
  history?: RealityHistoryMessage[];
}

//document type

interface RealityDocument {
  id: string;
  name: string;
  status: string;
  statusDetail: string | null;
  pageCount: number;
  sizeMb: number;
  createdAt: string;
  thumbnailUrl: string | null;
  enabled: boolean;
}

interface RealityDocumentsData {
  realityDocuments: {
    documents: RealityDocument[];
  };
}

// Document Tab

interface DocsViewProps {
  state: AppState;
  setState: React.Dispatch<React.SetStateAction<AppState>>;
}

interface RealityHistoryMessage {
  role: "user" | "assistant";
  content: string;
}

interface RealitySourcePage {
  pageIndex: number;
}

interface RealitySource {
  id: string;
  name: string;
  pages: RealitySourcePage[];
}

interface RealitySearchData {
  realitySearch: {
    answer: string;
    sources: RealitySource[];
    pagesViewedCount: number;
    durationMs: number | null;
  };
}

interface RealitySearchVars {
  message: string;
  history?: RealityHistoryMessage[];
}

export function DocsView({
  state,
  setState,
}: DocsViewProps): React.ReactElement {
  const products = [
    "All",
    ...uniq(state.docs.map((doc) => doc.product)),
  ];

  const {
  data: realityDocumentsData,
  loading: realityDocumentsLoading,
  error: realityDocumentsError,
  refetch: refetchRealityDocuments,
} = useQuery<RealityDocumentsData>(
  REALITY_DOCUMENTS_QUERY,
  {
    fetchPolicy: "no-cache",
  }
);

const realityDocuments =
  realityDocumentsData?.realityDocuments.documents ?? [];

  const versions = [
    "All",
    ...uniq(
      state.docs
        .filter((doc) =>
          state.docFilters.product === "All"
            ? true
            : doc.product === state.docFilters.product
        )
        .map((doc) => doc.version)
    ),
  ];

  const categories = [
    "All",
    ...uniq(state.docs.map((doc) => doc.category)),
  ];

  const localSearchQuery = (
    state.docFilters.query || ""
  )
    .toLowerCase()
    .trim();

  const filtered = state.docs.filter((doc) => {
    const filters = state.docFilters;

    const localMatch =
      !localSearchQuery ||
      doc.title.toLowerCase().includes(localSearchQuery) ||
      doc.body.toLowerCase().includes(localSearchQuery) ||
      doc.tags
        .join(" ")
        .toLowerCase()
        .includes(localSearchQuery) ||
      doc.id.toLowerCase().includes(localSearchQuery);

    return (
      (filters.product === "All" ||
        doc.product === filters.product) &&
      (filters.version === "All" ||
        doc.version === filters.version) &&
      (filters.category === "All" ||
        doc.category === filters.category) &&
      localMatch &&
      matchesGlobal(doc, state.globalQuery)
    );
  });

  const selected = filtered[0] || null;

  const updateFilters = (
    patch: Partial<typeof state.docFilters>
  ): void => {
    setState((previous) => ({
      ...previous,
      docFilters: {
        ...previous.docFilters,
        ...patch,
      },
    }));
  };

 



  // Reality Systems search state
  const [realityQuestion, setRealityQuestion] =
    useState<string>("");

  const [realityAnswer, setRealityAnswer] =
    useState<string>("");

  const [realitySources, setRealitySources] = useState<
    RealitySource[]
  >([]);

  const [realityHistory, setRealityHistory] = useState<
    RealityHistoryMessage[]
  >([]);

  const [realityError, setRealityError] = useState<
    string | null
  >(null);

  const [realityPagesViewed, setRealityPagesViewed] =
    useState<number>(0);

  const [realityDuration, setRealityDuration] = useState<
    number | null
  >(null);

  const [
    runRealitySearch,
    {
      loading: realityLoading,
    },
  ] = useLazyQuery<
    RealitySearchData,
    RealitySearchVars
  >(REALITY_SEARCH_QUERY, {
    fetchPolicy: "no-cache",
  });

  const searchRealityDocuments =
    async (): Promise<void> => {
      const question = realityQuestion.trim();

      if (!question || realityLoading) {
        return;
      }

      setRealityError(null);
      setRealityAnswer("");
      setRealitySources([]);
      setRealityPagesViewed(0);
      setRealityDuration(null);

      try {
        const result = await runRealitySearch({
          variables: {
            message: question,
            history: realityHistory,
          },
        });

        const response = result.data?.realitySearch;

        if (!response) {
          throw new Error(
            "Reality Systems returned no result."
          );
        }

        setRealityAnswer(response.answer);
        setRealitySources(response.sources);
        setRealityPagesViewed(
          response.pagesViewedCount
        );
        setRealityDuration(response.durationMs);

        setRealityHistory((previous) => [
          ...previous,
          {
            role: "user",
            content: question,
          },
          {
            role: "assistant",
            content: response.answer,
          },
        ]);

        setRealityQuestion("");
      } catch (error) {
        console.error(
          "REALITY SEARCH ERROR:",
          error
        );

        setRealityError(
          error instanceof Error
            ? error.message
            : "Unable to search Reality Systems documents."
        );
      }
    };

  const clearRealityConversation = (): void => {
    setRealityQuestion("");
    setRealityAnswer("");
    setRealitySources([]);
    setRealityHistory([]);
    setRealityError(null);
    setRealityPagesViewed(0);
    setRealityDuration(null);
  };

  return (
    <>
      <div className="split">
        {/* <div className="card">
          <div className="cardHeader">
            <div>
              <h3>Knowledge Base</h3>
              <p>
                Searchable docs by
                product/version/category.
              </p>
            </div>

            <span className="badge">
              Results {filtered.length}
            </span>
          </div>

          <div className="cardBody">
            <div className="filters">
              <div className="field">
                <label>Product</label>

                <select
                  value={
                    state.docFilters.product
                  }
                  onChange={(event) =>
                    updateFilters({
                      product:
                        event.target.value,
                      version: "All",
                    })
                  }
                >
                  {products.map((product) => (
                    <option
                      key={product}
                      value={product}
                    >
                      {product}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field">
                <label>Version</label>

                <select
                  value={
                    state.docFilters.version
                  }
                  onChange={(event) =>
                    updateFilters({
                      version:
                        event.target.value,
                    })
                  }
                >
                  {versions.map((version) => (
                    <option
                      key={version}
                      value={version}
                    >
                      {version}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field">
                <label>Category</label>

                <select
                  value={
                    state.docFilters.category
                  }
                  onChange={(event) =>
                    updateFilters({
                      category:
                        event.target.value,
                    })
                  }
                >
                  {categories.map(
                    (category) => (
                      <option
                        key={category}
                        value={category}
                      >
                        {category}
                      </option>
                    )
                  )}
                </select>
              </div>

              <div
                className="field"
                style={{
                  flex: 1,
                  minWidth: 240,
                }}
              >
                <label>Search</label>

                <input
                  value={
                    state.docFilters.query
                  }
                  onChange={(event) =>
                    updateFilters({
                      query:
                        event.target.value,
                    })
                  }
                  placeholder="Search title, tags, content…"
                />
              </div>
            </div>

            <div style={{ height: 14 }} />

            <table className="table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Product</th>
                  <th>Version</th>
                  <th>Category</th>
                  <th>Title</th>
                </tr>
              </thead>

              <tbody>
                {filtered.map((doc) => (
                  <tr
                    key={doc.id}
                    style={{
                      cursor: "pointer",
                    }}
                  >
                    <td
                      style={{
                        fontFamily:
                          "var(--mono)",
                      }}
                    >
                      {doc.id}
                    </td>

                    <td>{doc.product}</td>

                    <td>
                      <span className="badge">
                        {doc.version}
                      </span>
                    </td>

                    <td>{doc.category}</td>

                    <td
                      style={{
                        color:
                          "rgba(255,255,255,.88)",
                      }}
                    >
                      {doc.title}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filtered.length === 0 && (
              <>
                <div
                  style={{ height: 12 }}
                />

                <div className="empty">
                  No docs match your
                  filters.
                </div>
              </>
            )}
          </div>
        </div> */}

        {/* <div className="card">
          <div className="cardHeader">
            <div>
              <h3>Article</h3>

              <p>
                {selected
                  ? `${selected.product} · ${selected.version}`
                  : "Select an article"}
              </p>
            </div>

            <span className="badge">
              {selected
                ? selected.id
                : "—"}
            </span>
          </div>

          <div className="cardBody">
            {selected ? (
              <>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent:
                      "space-between",
                    gap: 12,
                    flexWrap: "wrap",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 800,
                        letterSpacing: ".2px",
                      }}
                    >
                      {selected.title}
                    </div>

                    <div
                      style={{
                        marginTop: 6,
                        fontSize: 12,
                        color:
                          "rgba(255,255,255,.70)",
                      }}
                    >
                      Category:{" "}
                      {selected.category}
                      {" · "}
                      Tags:{" "}

                      {selected.tags.map(
                        (tag) => (
                          <span
                            key={tag}
                            className="badge"
                          >
                            {tag}
                          </span>
                        )
                      )}
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: 10,
                    }}
                  >
                    <button
                      className="btn"
                      onClick={() =>
                        window.alert(
                          "Mock: Article link copied."
                        )
                      }
                    >
                      Copy link
                    </button>

                    <button
                      className="btn primary"
                      onClick={() =>
                        window.alert(
                          "Thanks! (Mock feedback captured.)"
                        )
                      }
                    >
                      Helpful
                    </button>
                  </div>
                </div>

                

                <div
                  style={{ height: 12 }}
                />

                <div
                  className="empty"
                  style={{
                    borderStyle: "solid",
                  }}
                >
                  <div
                    style={{
                      fontFamily:
                        "var(--mono)",
                      fontSize: 12,
                      color:
                        "rgba(255,255,255,.82)",
                      whiteSpace: "pre-wrap",
                      lineHeight: 1.45,
                    }}
                  >
                    {selected.body}
                  </div>
                </div>
              </>
            ) : (
              <div className="empty">
                Pick a doc on the left to
                preview it here.
              </div>
            )}
          </div>
        </div> */}
      </div>

      <div style={{ height: 18 }} />

      {/* Reality Documents Section */}

       <div style={{ height: 18 }} />

<div className="grid">
  <div
    className="card"
    style={{ gridColumn: "span 12" }}
  >
    <div className="cardHeader">
      <div>
        <h3>Reality Document Library</h3>
        <p>
          Documents currently indexed in Reality Systems.
        </p>
      </div>

      <button
        type="button"
        className="btn"
        disabled={realityDocumentsLoading}
        onClick={() => void refetchRealityDocuments()}
      >
        {realityDocumentsLoading
          ? "Refreshing..."
          : "Refresh"}
      </button>
    </div>

    <div className="cardBody">
      {realityDocumentsLoading && (
        <div className="empty">
          Loading Reality Systems documents...
        </div>
      )}

      {realityDocumentsError && (
        <div className="empty">
          Unable to load Reality Systems documents.
        </div>
      )}

      {!realityDocumentsLoading &&
        !realityDocumentsError && (
          <table className="table">
            <thead>
              <tr>
                <th>Document</th>
                <th>Status</th>
                <th>Pages</th>
                <th>Size</th>
                <th>Uploaded</th>
                <th>Search</th>
              </tr>
            </thead>

            <tbody>
              {realityDocuments.map((document) => (
                <tr key={document.id}>
                  <td>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                      }}
                    >
                      {document.thumbnailUrl && (
                        <img
                          src={document.thumbnailUrl}
                          alt=""
                          style={{
                            width: 42,
                            height: 54,
                            objectFit: "cover",
                            borderRadius: 4,
                          }}
                        />
                      )}

                      <div>
                        <strong>{document.name}</strong>

                        {document.statusDetail && (
                          <div
                            style={{
                              marginTop: 4,
                              fontSize: 12,
                              color:
                                "rgba(255,255,255,.65)",
                            }}
                          >
                            {document.statusDetail}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>

                  <td>
                    <span
                      className={`badge ${
                        document.status === "ready"
                          ? "ok"
                          : document.status === "error"
                          ? "danger"
                          : "warn"
                      }`}
                    >
                      ● {document.status}
                    </span>
                  </td>

                  <td>{document.pageCount}</td>

                  <td>
                    {document.sizeMb.toFixed(1)} MB
                  </td>

                  <td>
                    {new Date(
                      document.createdAt
                    ).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </td>

                  <td>
                    <span
                      className={`badge ${
                        document.enabled ? "ok" : ""
                      }`}
                    >
                      {document.enabled
                        ? "Enabled"
                        : "Disabled"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

      {!realityDocumentsLoading &&
        !realityDocumentsError &&
        realityDocuments.length === 0 && (
          <div className="empty">
            No Reality Systems documents found.
          </div>
        )}
    </div>
  </div>
</div>

      
    </>
  );
}



// Support Tab

interface SupportViewProps {
  state: AppState;
  setState: React.Dispatch<React.SetStateAction<AppState>>;
}

export function SupportView({
  state,
  setState,
}: SupportViewProps): React.ReactElement {
  const [realityQuestion, setRealityQuestion] = useState<string>("");
  const [realityAnswer, setRealityAnswer] = useState<string>("");

  const [realitySources, setRealitySources] = useState<
    RealitySource[]
  >([]);

  const [realityHistory, setRealityHistory] = useState<
    RealityHistoryMessage[]
  >([]);

  const [realityError, setRealityError] = useState<string | null>(
    null
  );

  const [realityPagesViewed, setRealityPagesViewed] =
    useState<number>(0);

  const [realityDuration, setRealityDuration] = useState<
    number | null
  >(null);

  const [runRealitySearch, { loading: realityLoading }] =
    useLazyQuery<RealitySearchData, RealitySearchVars>(
      REALITY_SEARCH_QUERY,
      {
        fetchPolicy: "no-cache",
      }
    );

  const canSeeOrg = state.role === "Org Admin";

  const statuses = [
    "All",
    ...uniq(state.tickets.map((ticket) => ticket.status)),
  ];

  const products = [
    "All",
    ...uniq(state.tickets.map((ticket) => ticket.product)),
  ];

  const filtered = state.tickets.filter((ticket) => {
    const filters = state.supportFilters;

    return (
      (filters.status === "All" ||
        ticket.status === filters.status) &&
      (filters.product === "All" ||
        ticket.product === filters.product) &&
      matchesGlobal(ticket, state.globalQuery)
    );
  });

  const selected = state.selectedTicketId
    ? state.tickets.find(
        (ticket) => ticket.id === state.selectedTicketId
      ) || null
    : filtered[0] || null;

  const updateFilters = (
    patch: Partial<typeof state.supportFilters>
  ): void => {
    setState((previous) => ({
      ...previous,
      supportFilters: {
        ...previous.supportFilters,
        ...patch,
      },
    }));
  };

  const setSelectedTicket = (id: string): void => {
    setState((previous) => ({
      ...previous,
      selectedTicketId: id,
    }));
  };

  const [replyText, setReplyText] = useState<string>("");

  const sendReply = (): void => {
    if (!selected || !replyText.trim()) {
      window.alert("Type a reply first.");
      return;
    }

    setState((previous) => ({
      ...previous,
      tickets: previous.tickets.map((ticket) =>
        ticket.id === selected.id
          ? {
              ...ticket,
              updated: todayStr(),
              messages: [
                ...ticket.messages,
                {
                  by: "Steve",
                  when: nowStr(),
                  text: replyText.trim(),
                },
              ],
            }
          : ticket
      ),
    }));

    setReplyText("");
    window.alert("Reply sent (mock).");
  };

  const closeTicket = (): void => {
    if (!selected) {
      return;
    }

    setState((previous) => ({
      ...previous,
      tickets: previous.tickets.map((ticket) =>
        ticket.id === selected.id
          ? {
              ...ticket,
              status: "Closed" as const,
              updated: todayStr(),
            }
          : ticket
      ),
    }));

    window.alert("Ticket closed (mock).");
  };

  const searchRealityDocuments = async (): Promise<void> => {
    const question = realityQuestion.trim();

    if (!question || realityLoading) {
      return;
    }

    setRealityError(null);
    setRealityAnswer("");
    setRealitySources([]);
    setRealityPagesViewed(0);
    setRealityDuration(null);

    try {
      const result = await runRealitySearch({
        variables: {
          message: question,
          history: realityHistory,
        },
      });

      const response = result.data?.realitySearch;

      if (!response) {
        throw new Error(
          "Reality Systems returned no search result."
        );
      }

      setRealityAnswer(response.answer);
      setRealitySources(response.sources);
      setRealityPagesViewed(response.pagesViewedCount);
      setRealityDuration(response.durationMs);

      setRealityHistory((previous) => [
        ...previous,
        {
          role: "user",
          content: question,
        },
        {
          role: "assistant",
          content: response.answer,
        },
      ]);

      setRealityQuestion("");
    } catch (error) {
      console.error("REALITY SUPPORT SEARCH ERROR:", error);

      setRealityError(
        error instanceof Error
          ? error.message
          : "Unable to search support documents."
      );
    }
  };

  const clearRealityConversation = (): void => {
    setRealityQuestion("");
    setRealityAnswer("");
    setRealitySources([]);
    setRealityHistory([]);
    setRealityError(null);
    setRealityPagesViewed(0);
    setRealityDuration(null);
  };

   //hubspot useState

  useEffect(() => {
  const scriptId = "hubspot-support-form-script";

  const existingScript = document.getElementById(scriptId);

  if (existingScript) {
    existingScript.remove();
  }

  const script = document.createElement("script");

  script.id = scriptId;
  script.src =
    "https://js.hsforms.net/forms/embed/47739181.js";
  script.defer = true;

  document.body.appendChild(script);

  return () => {
    script.remove();
  };
}, []);

  return (
    <div
      style={{
        height: "calc(100vh - 170px)",
        overflowY: "auto",
        overflowX: "hidden",
        paddingRight: 8,
      }}
    >
      {/* <div className="split">
        <div className="card">
          <div className="cardHeader">
            <div>
              <h3>Tickets</h3>

              <p>
                {canSeeOrg
                  ? "Org view enabled (Admin)."
                  : "My tickets only (mock)."}
              </p>
            </div>

            <button
              className="btn primary"
              onClick={() =>
                window.dispatchEvent(
                  new CustomEvent("open-ticket-modal")
                )
              }
            >
              New Ticket
            </button>
          </div>

          <div className="cardBody">
            <div className="filters">
              <div className="field">
                <label>Status</label>

                <select
                  value={state.supportFilters.status}
                  onChange={(event) =>
                    updateFilters({
                      status: event.target.value,
                    })
                  }
                >
                  {statuses.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field">
                <label>Product</label>

                <select
                  value={state.supportFilters.product}
                  onChange={(event) =>
                    updateFilters({
                      product: event.target.value,
                    })
                  }
                >
                  {products.map((product) => (
                    <option key={product} value={product}>
                      {product}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ height: 14 }} />

            <table className="table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Status</th>
                  <th>Product</th>
                  <th>Severity</th>
                  <th>Updated</th>
                </tr>
              </thead>

              <tbody>
                {filtered.map((ticket) => (
                  <tr
                    key={ticket.id}
                    style={{
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      setSelectedTicket(ticket.id)
                    }
                  >
                    <td
                      style={{
                        fontFamily: "var(--mono)",
                      }}
                    >
                      {ticket.id}
                    </td>

                    <td>
                      <BadgeForStatus status={ticket.status} />
                    </td>

                    <td>{ticket.product}</td>

                    <td>
                      <span
                        className={`badge ${severityClass(
                          ticket.severity
                        )}`}
                      >
                        ● {ticket.severity}
                      </span>
                    </td>

                    <td>{ticket.updated}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filtered.length === 0 && (
              <>
                <div style={{ height: 12 }} />

                <div className="empty">
                  No tickets match your filters.
                </div>
              </>
            )}
          </div>
        </div>

        <div className="card">
          <div className="cardHeader">
            <div>
              <h3>Ticket detail</h3>

              <p>
                {selected
                  ? `${selected.product} · ${selected.version}`
                  : "Select a ticket"}
              </p>
            </div>

            <span className="badge">
              {selected ? selected.id : "—"}
            </span>
          </div>

          <div className="cardBody">
            {selected ? (
              <div className="ticketDetail">
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 12,
                    flexWrap: "wrap",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 800,
                      }}
                    >
                      {selected.subject}
                    </div>

                    <div
                      style={{
                        marginTop: 6,
                        fontSize: 12,
                        color: "rgba(255,255,255,.70)",
                      }}
                    >
                      <BadgeForStatus
                        status={selected.status}
                      />

                      <span
                        style={{ marginLeft: 8 }}
                        className={`badge ${severityClass(
                          selected.severity
                        )}`}
                      >
                        ● {selected.severity}
                      </span>

                      <span
                        style={{ marginLeft: 8 }}
                        className="badge"
                      >
                        Owner: {selected.owner}
                      </span>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: 10,
                    }}
                  >
                    <button
                      className="btn"
                      onClick={() => {
                        // Reply input is below.
                      }}
                    >
                      Reply
                    </button>

                    <button
                      className="btn"
                      onClick={closeTicket}
                    >
                      Close
                    </button>
                  </div>
                </div>

                <div className="thread">
                  {selected.messages.map((message, index) => (
                    <div
                      key={index}
                      className={`msg ${
                        message.by === "Steve" ? "me" : ""
                      }`}
                    >
                      <div className="meta">
                        <span>
                          <strong>{message.by}</strong>
                        </span>

                        <span>{message.when}</span>
                      </div>

                      <div className="body">
                        {message.text}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="filters">
                  <div
                    className="field"
                    style={{
                      flex: 1,
                      minWidth: 240,
                    }}
                  >
                    <label>Add a reply</label>

                    <input
                      value={replyText}
                      onChange={(event) =>
                        setReplyText(event.target.value)
                      }
                      placeholder="Type a short update…"
                    />
                  </div>

                  <button
                    className="btn primary"
                    onClick={sendReply}
                  >
                    Send
                  </button>
                </div>
              </div>
            ) : (
              <div className="empty">
                Pick a ticket on the left to see details here.
              </div>
            )}
          </div>
        </div>
      </div> */}

      <div style={{ height: 18 }} />

      <div className="grid">
        <div
          className="card"
          style={{
            gridColumn: "span 12",
          }}
        >
          <div className="cardHeader">
            <div>
              <h3>AI Support Assistant</h3>

              <p>
                Search indexed technical documentation before
                opening or updating a support ticket.
              </p>
            </div>

            <span className="badge">
              {realityLoading
                ? "Searching"
                : "AI Support"}
            </span>
          </div>

          <div className="cardBody">
            <div className="field">
              <label>Describe your technical issue</label>

              <textarea
                value={realityQuestion}
                onChange={(event) =>
                  setRealityQuestion(event.target.value)
                }
                onKeyDown={(event) => {
                  if (
                    event.key === "Enter" &&
                    !event.shiftKey &&
                    !realityLoading
                  ) {
                    event.preventDefault();
                    void searchRealityDocuments();
                  }
                }}
                placeholder="For example: I am seeing latency spikes after configuring CPU shielding. What settings should I verify?"
                disabled={realityLoading}
                style={{
                  width: "100%",
                  minHeight: 110,
                  boxSizing: "border-box",
                  resize: "vertical",
                  padding: "10px 12px",
                  borderRadius: 10,
                  border:
                    "1px solid rgba(255,255,255,.14)",
                  background: "rgba(0,0,0,.16)",
                  color: "rgba(255,255,255,.92)",
                  font: "inherit",
                }}
              />
            </div>

            <div style={{ height: 12 }} />

            <div className="filters">
              <button
                type="button"
                className="btn primary"
                disabled={
                  realityLoading ||
                  !realityQuestion.trim()
                }
                onClick={() =>
                  void searchRealityDocuments()
                }
              >
                {realityLoading
                  ? "Searching..."
                  : "Search Support Documents"}
              </button>

              <button
                type="button"
                className="btn"
                disabled={realityLoading}
                onClick={clearRealityConversation}
              >
                Clear Conversation
              </button>
            </div>

            {realityLoading && (
              <>
                <div style={{ height: 12 }} />

                <div className="empty">
                  Searching indexed support documentation...
                </div>
              </>
            )}

            {realityError && (
              <>
                <div style={{ height: 12 }} />

                <div className="empty">
                  <strong>Search error</strong>
                  <br />
                  {realityError}
                </div>
              </>
            )}

            {realityAnswer && (
              <>
                <div style={{ height: 16 }} />

                <div
                  className="empty"
                  style={{
                    borderStyle: "solid",
                  }}
                >
                  <strong>Suggested resolution</strong>

                  <div
                    style={{
                      marginTop: 10,
                      lineHeight: 1.6,
                      whiteSpace: "pre-wrap",
                      color: "rgba(255,255,255,.88)",
                    }}
                  >
                    {realityAnswer}
                  </div>

                  <div
                    style={{
                      marginTop: 12,
                      display: "flex",
                      gap: 8,
                      flexWrap: "wrap",
                    }}
                  >
                    <span className="badge">
                      Pages reviewed: {realityPagesViewed}
                    </span>

                    {realityDuration !== null && (
                      <span className="badge">
                        Duration:{" "}
                        {(realityDuration / 1000).toFixed(2)}s
                      </span>
                    )}
                  </div>
                </div>

                <div style={{ height: 12 }} />

                <div className="filters">
                  <button
                    type="button"
                    className="btn primary"
                    onClick={() =>
                      window.dispatchEvent(
                        new CustomEvent(
                          "open-ticket-modal"
                        )
                      )
                    }
                  >
                    Still Need Help? Open Ticket
                  </button>
                </div>
              </>
            )}

            {realitySources.length > 0 && (
              <>
                <div style={{ height: 16 }} />

                <h4
                  style={{
                    margin: "0 0 10px",
                  }}
                >
                  Sources
                </h4>

                <div
                  className="grid"
                  style={{
                    gap: 10,
                  }}
                >
                  {realitySources.map((source) => (
                    <div
                      key={source.id}
                      className="stat"
                      style={{
                        gridColumn: "span 6",
                      }}
                    >
                      <div className="label">
                        Document
                      </div>

                      <div
                        className="value"
                        style={{
                          fontSize: 14,
                        }}
                      >
                        {source.name}
                      </div>

                      <div className="sub">
                        Pages:{" "}
                        {source.pages?.length
                          ? source.pages
                              .map(
                                (page) =>
                                  page.pageIndex + 1
                              )
                              .join(", ")
                          : "Not specified"}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          
          
<div style={{ height: 30 }} />

<div className="grid">
  <div
    className="card"
    style={{
      gridColumn: "span 12",
    }}
  >
    <div className="cardHeader">
      <div>
        <h3>Submit a Support Request</h3>

        <p>
          Complete the form below to contact the Concurrent
          Real-Time support team.
        </p>
      </div>

      <span className="badge">Support Form</span>
    </div>

    <div className="cardBody">
      <div
        className="hs-form-frame"
        data-region="na1"
        data-form-id="2a7138a8-1233-4031-9096-47787b87988e"
        data-portal-id="47739181"
        style={{
          width: "100%",
          minHeight: 500,
        }}
      />
    </div>
  </div>
</div>



        </div>
      </div>
      
    </div>

    
  );
}

//Account View //Organization

interface OrganizationInvitation {
  id: number;
  organization_id: number;
  email: string;
  role: "COMPANY_SUPERUSER" | "DEVELOPER";
  expires_at: string;
  accepted_at: string | null;
  created_at: string;
}

interface OrganizationInvitationsData {
  organizationInvitations: OrganizationInvitation[];
}

interface OrganizationInvitationsVars {
  portalUserId: number;
}

interface PortalOrganization {
  id: number;
  name: string;
  customer_id: number | null;
}

interface OrganizationMember {
  id: number;
  organization_id: number;
  portal_user_id: number;
  role: "COMPANY_SUPERUSER" | "DEVELOPER";
  status: "INVITED" | "ACTIVE" | "DISABLED";

  user: {
    id: number;
    name: string;
    email: string;
    login: string;
  };
}

interface MyOrganizationData {
  myOrganization: PortalOrganization | null;
}

interface MyOrganizationVars {
  portalUserId: number;
}

interface OrganizationMembersData {
  organizationMembers: OrganizationMember[];
}

interface OrganizationMembersVars {
  portalUserId: number;
}

interface InviteOrganizationUserData {
  inviteOrganizationUser: {
    id: number;
    organization_id: number;
    email: string;
    role: "COMPANY_SUPERUSER" | "DEVELOPER";
    expires_at: string;
    accepted_at: string | null;
  };
}

interface InviteOrganizationUserVars {
  portalUserId: number;
  email: string;
  role: "DEVELOPER";
}

interface AccountViewProps {
  state: AppState;
}

export function AccountView({
  state,
}: AccountViewProps): React.ReactElement {
  const storedUser = localStorage.getItem("user");
  const portalUser = storedUser
    ? JSON.parse(storedUser)
    : null;

  const [inviteEmail, setInviteEmail] =
    useState<string>("");

  const [inviteMessage, setInviteMessage] =
    useState<string | null>(null);

  const [inviteError, setInviteError] =
    useState<string | null>(null);

  // User management functions

  const [
  disableOrganizationUser,
  { loading: disableUserLoading },
] = useMutation(DISABLE_ORGANIZATION_USER);

const [
  enableOrganizationUser,
  { loading: enableUserLoading },
] = useMutation(ENABLE_ORGANIZATION_USER);

const [
  changeOrganizationUserRole,
  { loading: changeRoleLoading },
] = useMutation(CHANGE_ORGANIZATION_USER_ROLE);

const userManagementLoading =
  disableUserLoading ||
  enableUserLoading ||
  changeRoleLoading;

  // Existing maintenance query
  const {
    data,
    loading,
    error,
  } = useQuery<
    MyProductsData,
    MyProductsVars
  >(MY_PRODUCTS_QUERY, {
    variables: {
      portalUserId: portalUser?.id ?? 0,
    },
    skip: !portalUser?.id,
  });

  // Organization query
  const {
    data: organizationData,
    loading: organizationLoading,
    error: organizationError,
  } = useQuery<
    MyOrganizationData,
    MyOrganizationVars
  >(MY_ORGANIZATION_QUERY, {
    variables: {
      portalUserId: portalUser?.id ?? 0,
    },
    skip: !portalUser?.id,
  });

  // Organization members query
  const {
    data: membersData,
    loading: membersLoading,
    error: membersError,
    refetch: refetchMembers,
  } = useQuery<
    OrganizationMembersData,
    OrganizationMembersVars
  >(ORGANIZATION_MEMBERS_QUERY, {
    variables: {
      portalUserId: portalUser?.id ?? 0,
    },
    skip: !portalUser?.id,
  });

 //Invite query
 const {
  data: invitationsData,
  loading: invitationsLoading,
  error: invitationsError,
  refetch: refetchInvitations,
} = useQuery<
  OrganizationInvitationsData,
  OrganizationInvitationsVars
>(ORGANIZATION_INVITATIONS_QUERY, {
  variables: {
    portalUserId: portalUser?.id ?? 0,
  },
  skip: !portalUser?.id,
});

const invitations =
  invitationsData?.organizationInvitations ?? [];

  //Resend Invite mutation
  const [
  resendOrganizationInvitation,
  { loading: resendInviteLoading },
] = useMutation(RESEND_ORGANIZATION_INVITATION);

  // Invite mutation
  const [
    inviteOrganizationUser,
    { loading: inviteLoading },
  ] = useMutation<
    InviteOrganizationUserData,
    InviteOrganizationUserVars
  >(INVITE_ORGANIZATION_USER);

  // Cancel Invite mutation

  const [
  cancelOrganizationInvitation,
  { loading: cancelInviteLoading },
] = useMutation(CANCEL_ORGANIZATION_INVITATION);

  const products = data?.myProducts ?? [];

  const activeProducts = products.filter(
    (p) =>
      p.maint_through &&
      new Date(p.maint_through) >= new Date()
  );

  const expiredProducts = products.filter(
    (p) =>
      p.maint_through &&
      new Date(p.maint_through) < new Date()
  );

  const organization =
    organizationData?.myOrganization ?? null;

  const members =
    membersData?.organizationMembers ?? [];

  // Find the current logged-in user's membership
  const currentMembership =
    members.find(
      (member) =>
        member.portal_user_id === portalUser?.id
    ) ?? null;

  const isSuperuser =
    currentMembership?.role ===
      "COMPANY_SUPERUSER" &&
    currentMembership?.status === "ACTIVE";
  
  //handle invites

  const handleResendInvitation = async (
  invitationId: number
): Promise<void> => {
  if (!portalUser?.id) return;

  try {
    await resendOrganizationInvitation({
      variables: {
        portalUserId: portalUser.id,
        invitationId,
      },
    });

    await refetchInvitations();

    window.alert("Invitation resent.");
  } catch (err) {
    const message =
      err instanceof Error
        ? err.message
        : "Unable to resend invitation.";

    window.alert(message);
  }
};

//Organization handlers

const handleDisableUser = async (
  member: OrganizationMember
): Promise<void> => {
  if (!portalUser?.id) return;

  if (
    !window.confirm(
      `Disable ${member.user.name}'s portal access?`
    )
  ) {
    return;
  }

  try {
    await disableOrganizationUser({
      variables: {
        portalUserId: portalUser.id,
        membershipId: member.id,
      },
    });

    await refetchMembers();
  } catch (err) {
    window.alert(
      err instanceof Error
        ? err.message
        : "Unable to disable user."
    );
  }
};

const handleEnableUser = async (
  member: OrganizationMember
): Promise<void> => {
  if (!portalUser?.id) return;

  try {
    await enableOrganizationUser({
      variables: {
        portalUserId: portalUser.id,
        membershipId: member.id,
      },
    });

    await refetchMembers();
  } catch (err) {
    window.alert(
      err instanceof Error
        ? err.message
        : "Unable to enable user."
    );
  }
};

const handlePromoteUser = async (
  member: OrganizationMember
): Promise<void> => {
  if (!portalUser?.id) return;

  if (
    !window.confirm(
      `Promote ${member.user.name} to Company Superuser?`
    )
  ) {
    return;
  }

  try {
    await changeOrganizationUserRole({
      variables: {
        portalUserId: portalUser.id,
        membershipId: member.id,
        role: "COMPANY_SUPERUSER",
      },
    });

    await refetchMembers();
  } catch (err) {
    window.alert(
      err instanceof Error
        ? err.message
        : "Unable to promote user."
    );
  }
};

const handleDemoteUser = async (
  member: OrganizationMember
): Promise<void> => {
  if (!portalUser?.id) return;

  if (
    !window.confirm(
      `Change ${member.user.name} to Developer?`
    )
  ) {
    return;
  }

  try {
    await changeOrganizationUserRole({
      variables: {
        portalUserId: portalUser.id,
        membershipId: member.id,
        role: "DEVELOPER",
      },
    });

    await refetchMembers();
  } catch (err) {
    window.alert(
      err instanceof Error
        ? err.message
        : "Unable to change role."
    );
  }
};

  const handleCancelInvitation = async (
  invitationId: number
): Promise<void> => {
  if (!portalUser?.id) return;

  const confirmed = window.confirm(
    "Are you sure you want to cancel this invitation?"
  );

  if (!confirmed) return;

  try {
    await cancelOrganizationInvitation({
      variables: {
        portalUserId: portalUser.id,
        invitationId,
      },
    });

    await refetchInvitations();
  } catch (err) {
    const message =
      err instanceof Error
        ? err.message
        : "Unable to cancel invitation.";

    window.alert(message);
  }
};

  const handleInviteDeveloper =
  async (): Promise<void> => {
    const email =
      inviteEmail.trim().toLowerCase();

    setInviteMessage(null);
    setInviteError(null);

    if (!email) {
      setInviteError(
        "Enter an email address."
      );
      return;
    }

    try {
      const result =
        await inviteOrganizationUser({
          variables: {
            portalUserId: portalUser.id,
            email,
            role: "DEVELOPER",
          },
        });

      if (
        result.data?.inviteOrganizationUser
      ) {
        setInviteEmail("");

        setInviteMessage(
          `Invitation created for ${email}.`
        );

        await refetchInvitations();
      }
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Unable to invite user.";

      setInviteError(message);
    }
  };

  return (
    <div className="grid">

      {/* PROFILE */}

      <div
        className="card"
        style={{ gridColumn: "span 6" }}
      >
        <div className="cardHeader">
          <div>
            <h3>Profile</h3>
            <p>Your portal account.</p>
          </div>

          {currentMembership && (
            <span className="badge">
              {currentMembership.role ===
              "COMPANY_SUPERUSER"
                ? "Company Superuser"
                : "Developer"}
            </span>
          )}
        </div>

        <div className="cardBody">
          <div className="filters">

            <div
              className="field"
              style={{ flex: 1 }}
            >
              <label>Name</label>

              <input
              style={{ backgroundColor: "white" }}
                value={
                  portalUser?.name || ""
                }
                readOnly
              />
            </div>

            <div
              className="field"
              
              style={{ flex: 1 }}
            >
              <label>Email</label>

              <input
              style={{ backgroundColor: "white" }}
                value={
                  portalUser?.email || ""
                }
                readOnly
              />
            </div>

          </div>

          <div style={{ height: 12 }} />

          <div className="filters">

            <button className="btn">
              Change Password
            </button>

            <button className="btn">
              Enable MFA
            </button>

          </div>
        </div>
      </div>


      {/* MAINTENANCE */}

      <div
        className="card"
        style={{ gridColumn: "span 6" }}
      >
        <div className="cardHeader">
          <div>
            <h3>
              Maintenance Overview
            </h3>

            <p>
              Your active customer
              entitlements.
            </p>
          </div>
        </div>

        <div className="cardBody">

          {loading && (
            <div className="empty">
              Loading...
            </div>
          )}

          {error && (
            <div className="empty">
              Error loading maintenance.
            </div>
          )}

          {!loading && !error && (
            <>
              <div className="statRow">

                <div className="stat">
                  <div className="label">
                    Active Products
                  </div>

                  <div className="value">
                    {activeProducts.length}
                  </div>

                  <div className="sub">
                    Eligible for downloads
                  </div>
                </div>

                <div className="stat">
                  <div className="label">
                    Expired Products
                  </div>

                  <div className="value">
                    {expiredProducts.length}
                  </div>

                  <div className="sub">
                    Maintenance expired
                  </div>
                </div>

              </div>

              <div
                style={{ height: 20 }}
              />
            
              <div className="maintenanceTableScroll">
  <table className="table">
    <thead>
      <tr>
        <th>Product</th>
        <th>Version</th>
        <th>Maintenance Through</th>
        <th>Status</th>
      </tr>
    </thead>

    <tbody>
      {products.map((p) => {
        const active =
          p.maint_through &&
          new Date(p.maint_through) >= new Date();

        return (
          <tr key={p.id}>
            <td>{p.name}</td>

            <td>
              {p.selected_version || "—"}
            </td>

            <td>
              {p.maint_through
                ? new Date(
                    p.maint_through
                  ).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })
                : "—"}
            </td>

            <td>
              <span
                className={`badge ${
                  active ? "ok" : "warn"
                }`}
              >
                ● {active ? "Active" : "Expired"}
              </span>
            </td>
          </tr>
        );
      })}
    </tbody>
  </table>
</div>
            </>
          )}
        </div>
      </div>


      {/* ORGANIZATION USER MANAGEMENT */}

      <div
        className="card"
        style={{ gridColumn: "span 12" }}
      >
        <div className="cardHeader">

          <div>
            <h3>User Management</h3>

            <p>
              {organization
                ? `${organization.name} organization accounts`
                : "Organization accounts"}
            </p>
          </div>

          {currentMembership && (
            <span className="badge">
              {currentMembership.role ===
              "COMPANY_SUPERUSER"
                ? "Superuser"
                : "Developer"}
            </span>
          )}

        </div>

        <div className="cardBody">

          {organizationLoading ||
          membersLoading ? (
            <div className="empty">
              Loading organization...
            </div>
          ) : organizationError ||
            membersError ? (
            <div className="empty">
              Error loading organization
              information.
            </div>
          ) : !organization ? (
            <div className="empty">
              Your account is not assigned
              to an organization.
            </div>
          ) : (
            <>

              {/* ORGANIZATION SUMMARY */}

              <div className="statRow">

                <div className="stat">
                  <div className="label">
                    Organization
                  </div>

                  <div
                    className="value"
                    style={{ fontSize: 16 }}
                  >
                    {organization.name}
                  </div>
                </div>

                <div className="stat">
                  <div className="label">
                    Members
                  </div>

                  <div className="value">
                    {members.length}
                  </div>

                  <div className="sub">
                    Active portal accounts
                  </div>
                </div>

                <div className="stat">
                  <div className="label">
                    Your Role
                  </div>

                  <div
                    className="value"
                    style={{ fontSize: 14 }}
                  >
                    {isSuperuser
                      ? "Company Superuser"
                      : "Developer"}
                  </div>
                </div>

              </div>

              <div
                style={{ height: 20 }}
              />


              {/* INVITE USER */}

              {isSuperuser && (
                <>
                  <div
                    style={{
                      border:
                        "1px solid rgba(255,255,255,.10)",
                      borderRadius: 12,
                      padding: 16,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 800,
                        marginBottom: 4,
                      }}
                    >
                      Invite Developer
                    </div>

                    <div
                      style={{
                        fontSize: 12,
                        color:
                          "rgba(255,255,255,.65)",
                        marginBottom: 12,
                      }}
                    >
                      Add another developer
                      to your organization.
                    </div>

                    <div className="filters">

                      <div
                        className="field"
                        style={{
                          flex: 1,
                          minWidth: 260,
                        }}
                      >
                        <label>
                          Developer Email
                        </label>

                        <input
                          type="email"
                          value={inviteEmail}
                          onChange={(e) =>
                            setInviteEmail(
                              e.target.value
                            )
                          }
                          placeholder="developer@company.com"
                          disabled={
                            inviteLoading
                          }
                        />
                      </div>

                      <button
                        type="button"
                        className="btn primary"
                        disabled={
                          inviteLoading ||
                          !inviteEmail.trim()
                        }
                        onClick={() =>
                          void handleInviteDeveloper()
                        }
                      >
                        {inviteLoading
                          ? "Sending..."
                          : "Invite Developer"}
                      </button>

                    </div>

                    {inviteMessage && (
                      <>
                        <div
                          style={{
                            height: 10,
                          }}
                        />

                        <div className="empty">
                          {inviteMessage}
                        </div>
                      </>
                    )}

                    {inviteError && (
                      <>
                        <div
                          style={{
                            height: 10,
                          }}
                        />

                        <div className="empty">
                          <strong>
                            Invite failed
                          </strong>

                          <br />

                          {inviteError}
                        </div>
                      </>
                    )}
                  </div>

                  <div
                    style={{ height: 20 }}
                  />
                </>
              )}


              {/* MEMBERS TABLE */}

              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    {isSuperuser && (
                    <th>Action</th>)}

                  </tr>
                </thead>

                <tbody>
                  {members.map(
                    (member) => (
                      <tr key={member.id}>

                        <td>
                          {member.user.name}
                        </td>

                        <td>
                          {member.user.email}
                        </td>

                        <td>
                          <span className="badge">
                            {member.role ===
                            "COMPANY_SUPERUSER"
                              ? "Company Superuser"
                              : "Developer"}
                          </span>
                        </td>

                        <td>
                          <span
                            className={`badge ${
                              member.status ===
                              "ACTIVE"
                                ? "ok"
                                : member.status ===
                                  "DISABLED"
                                ? "warn"
                                : ""
                            }`}
                          >
                            ●{" "}
                            {member.status}
                          </span>
                        </td>
{/* This is the usermangement row that is used for only the super user. for now. */}
                        {isSuperuser && (
  <td>
    {member.portal_user_id === portalUser?.id ? (
      <span className="badge">
        Your Account
      </span>
    ) : (
      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        {member.role === "DEVELOPER" ? (
          <button
            type="button"
            className="btn"
            disabled={userManagementLoading}
            onClick={() =>
              void handlePromoteUser(member)
            }
          >
            Promote
          </button>
        ) : (
          <button
            type="button"
            className="btn"
            disabled={userManagementLoading}
            onClick={() =>
              void handleDemoteUser(member)
            }
          >
            Demote
          </button>
        )}

        {member.status === "ACTIVE" ? (
          <button
            type="button"
            className="btn"
            disabled={userManagementLoading}
            onClick={() =>
              void handleDisableUser(member)
            }
          >
            Disable
          </button>
        ) : (
          <button
            type="button"
            className="btn primary"
            disabled={userManagementLoading}
            onClick={() =>
              void handleEnableUser(member)
            }
          >
            Enable
          </button>
        )}
      </div>
    )}
  </td>
)}

                      </tr>
                    )
                  )}
                </tbody>
              </table>
              {isSuperuser && (
  <>
    <div style={{ height: 24 }} />

    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        marginBottom: 12,
      }}
    >
      <div>
        <div
          style={{
            fontSize: 14,
            fontWeight: 800,
          }}
        >
          Pending Invitations
        </div>

        <div
          style={{
            marginTop: 4,
            fontSize: 12,
            color: "rgba(255,255,255,.65)",
          }}
        >
          Developers who have been invited but have not
          created or linked their account yet.
        </div>
      </div>

      <span className="badge">
        {invitations.length} Pending
      </span>
    </div>

    {invitationsLoading && (
      <div className="empty">
        Loading invitations...
      </div>
    )}

    {invitationsError && (
      <div className="empty">
        Error loading pending invitations.
      </div>
    )}

    {!invitationsLoading &&
      !invitationsError &&
      invitations.length > 0 && (
        <div className="invitationTableScroll">
          <table className="table">
           <thead>
            <tr>
              <th>Email</th>
              <th>Role</th>
              <th>Invited</th>
              <th>Expires</th>
              <th>Status</th>
              <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {invitations.map((invite) => {
                const expired =
                  new Date(invite.expires_at) < new Date();

                return (
                  <tr key={invite.id}>
                    <td>{invite.email}</td>

                    <td>
                      <span className="badge">
                        {invite.role ===
                        "COMPANY_SUPERUSER"
                          ? "Company Superuser"
                          : "Developer"}
                      </span>
                    </td>

                    <td>
                      {new Date(
                        invite.created_at
                      ).toLocaleDateString(
                        "en-US",
                        {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        }
                      )}
                    </td>

                    <td>
                      {new Date(
                        invite.expires_at
                      ).toLocaleDateString(
                        "en-US",
                        {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        }
                      )}
                    </td>

                    <td>
                      <span
                        className={`badge ${
                          expired
                            ? "warn"
                            : ""
                        }`}
                      >
                        ●{" "}
                        {expired
                          ? "Expired"
                          : "Pending"}
                      </span>
                    </td>
                       <td>
  <div
    style={{
      display: "flex",
      gap: 8,
      alignItems: "center",
      flexWrap: "wrap",
    }}
  >
    <button
      type="button"
      className="btn"
      disabled={
        resendInviteLoading ||
        cancelInviteLoading
      }
      onClick={() =>
        void handleResendInvitation(invite.id)
      }
    >
      {resendInviteLoading
        ? "Resending..."
        : "Resend"}
    </button>

    <button
      type="button"
      className="btn"
      disabled={
        cancelInviteLoading ||
        resendInviteLoading
      }
      onClick={() =>
        void handleCancelInvitation(invite.id)
      }
    >
      Cancel
    </button>
  </div>
</td>



                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

    {!invitationsLoading &&
      !invitationsError &&
      invitations.length === 0 && (
        <div className="empty">
          No pending invitations.
        </div>
      )}
  </>
)}

              {members.length === 0 && (
                <>
                  <div
                    style={{
                      height: 12,
                    }}
                  />

                  <div className="empty">
                    No organization members
                    found.
                  </div>
                </>
              )}

              {!isSuperuser && (
                <>
                  <div
                    style={{
                      height: 16,
                    }}
                  />

                  <div className="empty">
                    Only a company superuser
                    can invite or manage
                    organization users.
                  </div>
                </>
              )}

            </>
          )}
        </div>
      </div>

    </div>
  );
}